import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Filter, X } from 'lucide-react';
import { SearchResult } from '../types';
import { searchEngine, SearchFilters } from '../utils/searchUtils';
import SearchBar from '../components/SearchBar';
import SearchResults from '../components/SearchResults';

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [activeFilters, setActiveFilters] = useState<SearchFilters>({});

  const query = searchParams.get('q') || '';

  useEffect(() => {
    if (query) {
      performSearch(query, filters);
    }
  }, [query, filters]);

  const performSearch = async (query: string, searchFilters?: SearchFilters) => {
    setIsLoading(true);
    
    // Simulate search delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Use the search engine to get results
    const searchResults = searchEngine.search(query, searchFilters);
    setResults(searchResults);
    setActiveFilters(searchFilters || {});
    setIsLoading(false);
  };

  const handleSearch = (query: string, searchFilters?: SearchFilters) => {
    setFilters(searchFilters || {});
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setActiveFilters({});
  };

  const hasActiveFilters = Object.values(activeFilters).some(value => value !== undefined);

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <div className="content-card">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Search Documentation</h1>
        <SearchBar 
          placeholder="Search for help articles, guides, and documentation..."
          onSearch={handleSearch}
          showFilters={true}
        />
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="content-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Active Filters</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(activeFilters).map(([key, value]) => (
              value && (
                <span
                  key={key}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {key}: {value}
                  <button
                    onClick={() => handleFilterChange(key as keyof SearchFilters, undefined)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {query && (
        <div className="content-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Search Results for "{query}"
            </h2>
            <div className="flex items-center space-x-4">
              {!isLoading && (
                <p className="text-sm text-gray-500">
                  {results.length} result{results.length !== 1 ? 's' : ''} found
                </p>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  showFilters 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={filters.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Categories</option>
                    {searchEngine.getCategories().map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Type
                  </label>
                  <select
                    value={filters.type || ''}
                    onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="page">Pages</option>
                    <option value="section">Sections</option>
                    <option value="subsection">Subsections</option>
                  </select>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <select
                    value={filters.dateRange || ''}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    <option value="">All Time</option>
                    <option value="week">Last Week</option>
                    <option value="month">Last Month</option>
                    <option value="year">Last Year</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Search Results Component */}
          <SearchResults 
            results={results}
            query={query}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Search Tips */}
      {!query && (
        <div className="content-card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Search Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Use specific keywords</h3>
              <p className="text-gray-600 text-sm">
                Instead of "how to invoice", try "create invoice" or "invoice template"
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Search by feature</h3>
              <p className="text-gray-600 text-sm">
                Look for specific features like "bank reconciliation" or "customer portal"
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Use action words</h3>
              <p className="text-gray-600 text-sm">
                Start with verbs like "create", "edit", "delete", "export", or "import"
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Browse categories</h3>
              <p className="text-gray-600 text-sm">
                Use the sidebar to explore specific sections of our documentation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Popular Categories */}
      {!query && (
        <div className="content-card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {searchEngine.getCategories().map(category => (
              <button
                key={category}
                onClick={() => navigate(`/search?q=${category.toLowerCase()}`)}
                className="p-4 text-center bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="font-medium text-gray-900">{category}</h3>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
